import {
  action,
  computed,
  makeAutoObservable,
  observable,
  reaction,
  toJS,
} from 'mobx';
import { validationContext } from 'validx';
import moment from 'moment';
import _ from 'lodash';
import Agent from 'env/agent';

import authStore from './authStore';
import { StoreBaseMethods } from './storeBase';
import { toLocalTime } from 'env/utils/dateUtil';
import { getShiftsFromResources } from 'env/utils/shiftsHelper';
import { loadDataHelper, saveDateHelper } from './storeHelpers';
import { visitorValidationSchema } from './validationSchemas/visitorValidationSchema';
import { isServer } from 'env/ssr/ServerSideRenderManager';
import { clientConfig } from 'env/config';
import { isTreatmentRoomsGroup } from 'env/utils/treatmentRoomsGroup';

const validation = validationContext();
class BookingsStore {
  host = null;
  constructor({ data, host, authTokens } = {}) {
    StoreBaseMethods.copyData(data, this);

    this.authTokens = authTokens;
    this.host = host || data?.host;

    // Maps are serialised as arrays by the server, so we need to convert them back to maps if we get them clientside
    this.trackedChanges = data?.trackedChanges
      ? new Map(data.trackedChanges)
      : observable.map({});
    this.newVisitorValidation = validation.reset();

    this.fromTime = moment().add(1, 'hour').startOf('hour').toDate();
    this.toTime = moment(this.fromTime).clone().add(1, 'hour').toDate();
    makeAutoObservable(this, undefined, { autoBind: true });

    reaction(
      () => toJS(this.newVisitor),
      (newVisitor) => {
        this.newVisitorValidation = validation
          .reset()
          .validate(newVisitor, visitorValidationSchema());
      },
    );

    if (!isServer()) {
      //These handlers are triggered by the signalR hub (signalr.js)
      window.sensorUpdated = async (data) => {
        this.loadSensors();
      };
    }
  }

  get agent() {
    return new Agent(this.host, this.authTokens);
  }

  @observable isLoadingSensors = false;
  @observable hasLoadedSensors = false;
  @observable sensors = [];
  @action loadSensors() {
    return loadDataHelper({
      store: this,
      agentKey: 'Bookings',
      key: 'Sensors',
    });
  }

  @observable isLoadingMyVisitors = false;
  @observable hasLoadedMyVisitors = false;
  @observable myVisitors = [];
  @action loadMyVisitors() {
    return loadDataHelper({
      store: this,
      agentKey: 'Bookings',
      key: 'MyVisitors',
      path: 'Visitors',
    });
  }

  @observable isLoadingPreviousVisitors = false;
  @observable hasLoadedPreviousVisitors = false;
  @observable previousVisitors = [];
  @action loadPreviousVisitors() {
    return loadDataHelper({
      store: this,
      agentKey: 'Bookings',
      key: 'PreviousVisitors',
      path: 'PreviousVisitors',
    });
  }

  @observable isLoadingTeamAttendance = true;
  @observable hasLoadedTeamAttendance = false;
  @observable teamsAttendance = [];
  @action loadTeamsAttendance(date) {
    return loadDataHelper({
      store: this,
      agentKey: 'Bookings',
      key: 'TeamAttendance',
      params: { date },
    }).then(
      action((data) => {
        this.teamsAttendance = data;

        return data;
      }),
    );
  }

  @action updateCoworkerAttendance(updatedCoworkerDetails) {
    const updatedTeamsAttendance = this.teamsAttendance?.map((team) => {
      const updatedCoworkers = team.Coworkers?.map((coworker) => {
        if (coworker.CoworkerId === updatedCoworkerDetails.CoworkerId) {
          return updatedCoworkerDetails;
        }

        return coworker;
      }, []);

      return {
        ...team,
        Coworkers: updatedCoworkers,
      };
    });

    this.updateTeamsAttendance(updatedTeamsAttendance);
  }

  @action updateTeamsAttendance(updatedTeamsDetails) {
    this.teamsAttendance = _.cloneDeep(updatedTeamsDetails);
  }

  @action getTeamAttendance(teamId) {
    return this.teamsAttendance.find(({ TeamId }) => TeamId === teamId);
  }

  @action getCoworkerAttendanceDetails(teamId) {
    const teamAttendanceDetails = this.getTeamAttendance(teamId);

    const coworkersAttendanceDetails = teamAttendanceDetails.Coworkers.map(
      (coworker) => {
        const attendanceDetails = Object.keys(coworker).reduce(
          (acc, currKey) => {
            if (currKey.includes('Attendance')) {
              return { ...acc, [currKey]: coworker[currKey] };
            }

            return { ...acc };
          },
          {},
        );

        return {
          CoworkerId: coworker.CoworkerId,
          Attendance: attendanceDetails,
        };
      },
    );

    return coworkersAttendanceDetails;
  }

  @observable isSavingTeamAttendance = false;
  @action saveTeamAttendance(updatedTeamDetails) {
    return saveDateHelper({
      store: this,
      agentKey: 'Bookings',
      key: `TeamAttendance`,
      data: updatedTeamDetails,
    });
  }

  @observable isLoadingAvailabilityAt = false;
  @observable hasLoadedAvailabilityAt = false;
  @observable availabilitiesAt = {};
  @observable availabilityAt = {};
  @action loadAvailabilityAt({
    customer,
    guid,
    startTime,
    interval = 30,
    bookingId,
  }) {
    return loadDataHelper({
      store: this,
      agentKey: 'Bookings',
      key: 'AvailabilityAt',
      params: { customer, guid, startTime, interval, bookingId },
    }).then(
      action((data) => {
        this.availabilitiesAt[guid] = data;
        return data;
      }),
    );
  }

  @computed get shifts() {
    if (!this.resourceSearch) return [];
    return getShiftsFromResources(
      this.resourceSearch.Resources,
      'ShiftsExpression',
    );
  }

  @action selectDates(dates) {
    Object.keys(dates).forEach((key) => {
      this[key] = dates[key];
    });
  }

  @observable fromTime = null;
  @observable toTime = null;
  @observable shiftId;
  @action selectShift(shiftId) {
    const shift = this.shifts[shiftId];
    if (!shift) {
      this.shiftId = shiftId;
      return;
    }

    const shiftStart = shift.start.clone();
    const shiftEnd = shift.end.clone();

    const start = moment(this.fromTime).clone();
    start.set('minutes', shiftStart.get('minutes'));
    start.set('hours', shiftStart.get('hours'));

    const end = moment(this.toTime).clone();
    end.set('minutes', shiftEnd.get('minutes'));
    end.set('hours', shiftEnd.get('hours'));

    if (start > end) {
      end.add(1, 'day');
    }

    this.fromTime = start.toDate();
    this.toTime = end.toDate();
    this.shiftId = parseInt(shiftId);
  }

  getResourceColor(resourceId, hex) {
    const bookingHighlightColorArray = [
      'green',
      'yellow',
      'orange',
      'red',
      'blue',
    ];
    var colors = [
      '#28B95F',
      '#DCB40A',
      '#F0783C',
      '#14B4E6',
      '#F04B69',
      '#0064FA',
      '#5F00BA',
      '#EE589E',
    ];
    var index = this.resources.findIndex((x) => x.Id == resourceId);
    var adjustedIndex = index % bookingHighlightColorArray.length;

    return hex
      ? colors[adjustedIndex > -1 ? adjustedIndex : 0]
      : bookingHighlightColorArray[adjustedIndex > -1 ? adjustedIndex : 0];
  }

  @observable isLoadingResources = false;
  @observable hasLoadedResources = false;
  @observable resources = [];
  @observable networkResources = [];
  @action loadResources() {
    return loadDataHelper({
      store: this,
      agentKey: 'Bookings',
      key: 'Resources',
      onDataReceived: (data) => {
        this.resources = data.Resources;
        this.networkResources = data.NetworkResources;
      },
    });
  }

  @computed get resourceMap() {
    return _.keyBy(this.resources, 'Id');
  }

  @observable isLoadingUnpaidBookings = false;
  @observable hasLoadedUnpaidBookings = false;
  @observable unpaidBookings = [];
  @action loadUnpaidBookings() {
    return loadDataHelper({
      store: this,
      agentKey: 'Bookings',
      key: 'UnpaidBookings',
      onDataReceived: (data) => {
        this.unpaidBookings = data;
        this.hasLoadedUnpaidBookings = true;
        this.isLoadingUnpaidBookings = false;
      },
    });
  }

  @observable cancellationDetails = {
    cancellationFee: 0,
    hasCancellationFee: false,
  };
  @action loadBookingCancellationFeeDetails(bookingId) {
    return loadDataHelper({
      store: this,
      agentKey: 'Bookings',
      key: 'CancellationFee',
      params: { bookingId },
      onDataReceived: (data) => {
        this.cancellationDetails = data;
      },
    });
  }

  @computed get resourceTypes() {
    return _(this.resources)
      .sortBy('DisplayOrder')
      .groupBy((r) => r.ResourceType.Id)
      .orderBy((resources) => resources[0].DisplayOrder)
      .map((resources) => {
        return {
          resources,
          ...resources[0].ResourceType,
        };
      })
      .value();
  }

  @computed get firstResourceType() {
    if (this.resources.length == 0) return null;
    return _(this.resources).sortBy('DisplayOrder').value()[0].ResourceType;
  }

  @computed get resourceGroups() {
    return _(this.resources)
      .groupBy((r) => r.GroupName)
      .orderBy((resources) => resources[0].GroupName)
      .map((resources, groupName) => {
        return {
          resources,
          groupName: resources[0].GroupName,
        };
      })
      .value();
  }

  @observable isLoadingResourceSearch = false;
  @observable hasLoadedResourceSearch = false;
  @observable resourceSearch = {
    Resources: [],
  };
  @action loadResourceSearch(params) {
    const homeBusinessId = authStore.customer?.InvoicingBusinessId;
    const restrictedParams =
      isTreatmentRoomsGroup(params?.group) && homeBusinessId
        ? { ...params, businessId: homeBusinessId }
        : params;
    return loadDataHelper({
      store: this,
      agentKey: 'Bookings',
      key: 'ResourceSearch',
      params: {
        ...restrictedParams,
        start: moment(this.fromTime).format('YYYY-MM-DDTHH:mm'),
        end: moment(this.toTime).format('YYYY-MM-DDTHH:mm'),
      },
    }).then(
      action((data) => {
        if (!params.skipShifts && this.shifts.length > 0 && !this.shiftId)
          this.selectShift(this.shifts[0].id);
        return data;
      }),
    );
  }

  @observable isLoadingBookings = false;
  @observable hasLoadedBookings = false;
  @observable bookings = [];
  @action loadBookings({
    start,
    end,
    resourceId,
    showAll,
    onlyEvents,
    resourceTypeId,
    group,
    ...rest
  }) {
    return loadDataHelper({
      store: this,
      agentKey: 'Bookings',
      key: 'Bookings',
      params: {
        start,
        end,
        resourceId,
        showAll,
        onlyEvents,
        resourceTypeId,
        group,
        ...rest,
      },
    });
  }

  @action deleteBooking(bookingId) {
    return this.agent.Bookings.deleteBooking(bookingId);
  }

  @action closeBooking() {
    this.booking = null;
    this.hasLoadedBooking = false;
  }

  @action closeNewBooking() {
    this.booking = null;
    this.hasLoadedBooking = false;
  }

  @action newBooking({
    fromTime,
    toTime,
    resourceId,
    floorPlanDeskId,
    deskName,
  }) {
    const now = fromTime || new Date();
    const ms = 15 * 60000;
    let roundedDate = new Date(Math.round(now.getTime() / ms) * ms);

    const { rltRestrictedResourceIds } = clientConfig();
    const isRlt = rltRestrictedResourceIds.includes(resourceId);
    const defaultDurationMinutes = isRlt ? 30 : 60;
    const resolvedToTime = isRlt
      ? new Date(roundedDate.getTime() + defaultDurationMinutes * 60000)
      : (toTime ??
        new Date(roundedDate.getTime() + defaultDurationMinutes * 60000));

    this.booking = {
      Id: 0,
      FromTime: roundedDate,
      ResourceId: resourceId,
      FloorPlanDeskId: floorPlanDeskId,
      DeskName: deskName,
      ResourceName:
        resourceId &&
        this.resources &&
        _.find(this.resources, (r) => r.Id == resourceId)?.Name,
      ToTime: resolvedToTime,
      BookingVisitors: [],
      BookingProducts: [],
    };
    this.resourceProducts = [];
  }

  @observable isDeletingVisitor = false;
  @action deleteVisitor(visitor) {
    this.isDeletingVisitor = true;
    return this.agent.Bookings.deleteVisitor(visitor).finally(
      action((data) => {
        this.isDeletingVisitor = false;
        return data;
      }),
    );
  }
  @action configureHomeLocationHost(hostname) {
    this.homeHost = hostname;
  }

  @observable newVisitorValidation = null;
  @observable isSavingNewVisitor = false;
  @observable newVisitor = {
    FullName: null,
    Email: null,
  };
  @action saveNewVisitor() {
    const newVisitorClone = {
      ...this.newVisitor,
      ExpectedArrival: toLocalTime(this.newVisitor.ExpectedArrival),
    };
    return saveDateHelper({
      store: { ...this, host: this.homeHost },
      agentKey: 'Bookings',
      key: `NewVisitor`,
      data: newVisitorClone,
      modelToValidate: newVisitorClone,
      validation: validation,
      validationSchema: visitorValidationSchema(),
    }).then(
      action((data) => {
        if (data.ErrorMessage) throw new Error(data.ErrorMessage);
        this.newVisitorValidation = validation.reset();
        return data;
      }),
    );
  }

  @observable isSavingBookingToCreate = false;
  @observable isSavingBookingToUpdate = false;
  @action saveBooking() {
    const bookingData = getBookingToSubmit(this.booking, this.resourceProducts);
    const agentAction = bookingData.booking.Id > 0 ? 'Update' : 'Create';
    this[`bookingTo${agentAction}`] = bookingData;

    // now booking is being saved, reset bookingHasChanged flag CD-444
    this.bookingHasChanged = false;

    return saveDateHelper({
      store: this,
      agentKey: 'Bookings',
      key: `BookingTo${agentAction}`,
    });
  }

  @action loadBasketBooking(booking) {
    this.booking = booking;
    this.booking.FromTime = new Date(this.booking.FromTime);
    this.booking.ToTime = new Date(this.booking.ToTime);
    this.loadResourceProducts({
      bookingId: 0,
      resourceId: booking.ResourceId,
    }).then(action(() => this.loadBookingPrice(booking)));
  }

  @observable isLoadingBookingPrice = false;
  @observable hasLoadedBookingPrice = false;
  @observable bookingPrice = [];
  loadBookingPrice = _.debounce(this.loadBookingPriceDebounced, 1000);

  @action loadBookingPriceDebounced(booking) {
    this.bookingPrice = null;
    this.hasLoadedBookingPrice = false;
    const bookingToProcess = booking ?? this.booking;
    if (!bookingToProcess) return Promise.resolve();

    return loadDataHelper({
      store: this,
      agentKey: 'Bookings',
      key: 'BookingPrice',
      params: getBookingToSubmit(bookingToProcess, this.resourceProducts),
    });
  }

  @action resetBookingPrice() {
    this.hasLoadedBookingPrice = false;
  }

  @observable isLoadingBooking = false;
  @observable hasLoadedBooking = false;
  @observable booking = null;
  @action loadBooking(bookingId) {
    this.booking = null;
    return loadDataHelper({
      store: this,
      agentKey: 'Bookings',
      key: 'Booking',
      path: 'Value',
      params: bookingId,
    })
      .then(
        action((data) => {
          if (!data) return;

          // const fromDate = new Date(data.FromTime);
          // const toDate = new Date(data.ToTime);
          // const fromHours = fromDate.getHours();
          // const fromOffset = fromDate.getTimezoneOffset() / 60.0;
          // const toHours = toDate.getHours();
          // const toOffset = toDate.getTimezoneOffset() / 60.0;
          // fromDate.setHours(fromHours + fromOffset);
          // toDate.setHours(toHours + toOffset);

          // data.FromTime = fromDate;
          // data.ToTime = toDate;
          data.BookingProducts = data.BookingProducts || [];
          data.BookingVisitors = data.BookingVisitors || [];

          this.booking = data;
          return data;
        }),
      )
      .then(
        action((data) => {
          if (!this.booking) return;
          this.loadBookingPrice(this.booking);
          return this.loadResourceProducts({
            bookingId: this.booking.Id,
            resourceId: this.booking.ResourceId,
          });
        }),
      );
  }

  @observable isLoadingResourceProducts = false;
  @observable hasLoadedResourceProducts = false;
  @observable resourceProducts = [];
  @action loadResourceProducts({ bookingId, resourceId }) {
    this.resourceProducts = [];
    return loadDataHelper({
      store: this,
      agentKey: 'Bookings',
      key: 'ResourceProducts',
      params: { bookingId, resourceId },
    }).then(
      action((data) => {
        var resourceProducts = [...data];
        if (this.booking && this.booking.ResourceProducts) {
          for (let index = 0; index < resourceProducts.length; index++) {
            const product = resourceProducts[index];
            var foundInBooking = this.booking.ResourceProducts.filter(
              (bp) => bp.ProductId == product.ProductId,
            );

            if (foundInBooking.length > 0) {
              product.Quantity = foundInBooking[0].Quantity;
              product.Selected =
                !product.RequestQuantity && product.Quantity >= 1;
            }
          }
          this.resourceProducts = resourceProducts;
        }
        return data;
      }),
    );
  }

  @observable isLoadingUpcomingBookings = false;
  @observable hasLoadedUpcomingBookings = false;
  @observable upcomingBookings = [];
  @action loadUpcomingBookings() {
    return loadDataHelper({
      store: this,
      agentKey: 'Bookings',
      key: 'UpcomingBookings',
      path: 'MyBookings',
    });
  }

  @observable isLoadingTeamBookings = false;
  @observable hasLoadedTeamBookings = false;
  @observable teamBookings = [];
  @action loadTeamBookings() {
    return loadDataHelper({
      store: this,
      agentKey: 'Bookings',
      key: 'TeamBookings',
      path: 'TeamBookings',
    });
  }

  // BW Custom Actions
  trackedProps = ['ToTime', 'FromTime', 'ResourceId'];
  trackedChanges = observable.map({});
  @observable originalBooking = null;
  @computed get hasBookingChanged() {
    return this.trackedChanges.size > 0;
  }

  @action setOriginalBooking = () => {
    if (!this.originalBooking) {
      this.originalBooking = this.booking;
    }
  };

  @action trackChange = (prop, value) => {
    // specific function to compare tracked changes
    const compareValues = (a, b) => {
      // handle comparing from/to times
      if (prop == 'ToTime' || prop == 'FromTime') {
        return new Date(a).getTime() == b.getTime();
      }
      // handle comparing resources
      if (prop == 'ResourceId') return a == b;
    };

    this.toTime = moment(this.fromTime).clone().add(1, 'hour').toDate();

    // if value is same as original booking, remove prop from Map

    if (
      this.originalBooking &&
      compareValues(this.originalBooking[prop], value)
    ) {
      this.trackedChanges.delete(prop);
    } else {
      this.trackedChanges.set(prop, value);
    }
  };

  // TODO: Kara- observable.map seems to just give an undefined - what even is it?
  @action resetBookingChangeTracking = () => {
    this.originalBooking = null;
    this.trackedChanges.clear();
    this.bookingHasChanged = false;
  };
  // End of BW Custom Actions

  @action initialiseNewBooking(bookingData) {
    this.booking = bookingData;
    this.booking.FromTime = new Date(this.booking.FromTime);
    this.booking.ToTime = new Date(this.booking.ToTime);
  }
}

export const getBookingToSubmit = (booking, resourceProducts = []) => {
  return {
    booking: {
      ...booking,
      FromTime: toLocalTime(booking.FromTime),
      ToTime: toLocalTime(booking.ToTime),
      resource: {
        Id: booking.ResourceId,
      },
      floorPlanDesk: {
        Id: booking.FloorPlanDeskId,
      },
    },
    products: resourceProducts,
    coworker: authStore.customer,
  };
};

export default BookingsStore;
